import React from 'react';
import {
  FieldValues,
  useFieldArray,
  UseFieldArrayProps,
  UseFormSetValue,
} from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  DeleteOutline,
  PercentOutlined,
} from '@mui/icons-material';
import { FormInput } from 'components';
import { DiscountTypes } from 'api';

type Props<T extends FieldValues = any> = UseFieldArrayProps<T> & {
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
};

const TiersInputs: React.FC<Props> = (props: Props): JSX.Element => {
  const { name, keyName, shouldUnregister, rules, control, setValue } = props;

  const appendValue = {
    percent_off: 0,
    qty_threshold: 0,
    threshold_error: null,
    percent_off_error: null,
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName,
    shouldUnregister,
    rules,
  });

  return (
    <Stack spacing={2}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h5">Quantity tiers</Typography>
      </Box>
      {fields.map((field, index) => (
        <Grid
          container
          key={field.id}
          columnGap={2}
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item flexGrow={1}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Grid container columnGap={2} wrap="nowrap">
                <Grid item xs={6}>
                  <FormInput
                    control={control}
                    name={`${name}.${index}.qty_threshold`}
                    inputType="number"
                    label="Min quantity"
                    rules={{
                      required: true,
                      min: 0,
                      onBlur: (e) =>
                        setValue(
                          `metafields_attributes.${0}.value.qty_tiers.${index}.qty_threshold`,
                          +(+e.target.value).toFixed(2),
                        ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    control={control}
                    name={`${name}.${index}.percent_off`}
                    inputType="number"
                    label="Percentage"
                    rules={{
                      required: true,
                      onBlur: (e) =>
                        setValue(
                          `metafields_attributes.${0}.value.qty_tiers.${index}.percent_off`,
                          +(+e.target.value).toFixed(2),
                        ),
                      min: 0,
                      max: 100,
                    }}
                    InputProps={{
                      endAdornment: <PercentOutlined />,
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item>
            <IconButton
              onClick={() => remove(index)}
              sx={(t) => ({
                background: t.palette.error.main,
                color: t.palette.common.white,
                '&:focus,&:hover': {
                  background: t.palette.error.dark,
                },
              })}
            >
              <DeleteOutline />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        fullWidth
        onClick={() => append(appendValue)}
        endIcon={<AddOutlined />}
      >
        Add more
      </Button>
    </Stack>
  );
};

export default TiersInputs;
